import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlert } from '@sorare/core/src/atoms/icons/system/iconAlert.ts';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';

import type { TransferType } from '#src/components/token/TokenTransferValidator/types.ts';

const WarningWrapper = styled.div`
  border-radius: var(--unit);
  padding: var(--unit) var(--intermediate-unit);
  background: rgba(var(--c-rgb-red-400), 0.1);
  border: 1px solid var(--c-red-300);
  color: var(--c-white);
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--unit);
`;

const IconWrapper = styled.div`
  font: var(--t-14);
`;

const Error = styled.b`
  color: var(--c-red-300);
`;

const ErrorBold = (...chunks: ReactNode[]) => <Error>{chunks}</Error>;

const messages = defineMessages({
  sealTitle: {
    defaultMessage: 'Sealing this card will unregister:',
  },
  unsealTitle: {
    defaultMessage: 'Unsealing this card will unregister:',
  },
  listTitle: {
    defaultMessage: 'Listing this card will unregister:',
  },
  sendTradeTitle: {
    defaultMessage: 'Sending a trade including this card will unregister:',
  },
  receiveTradeTitle: {
    defaultMessage: 'Accepting this trade will unregister:',
  },
  liveLineupWarning: {
    defaultMessage: '<e>Live</e> <b>{competition}</b> lineup',
  },
  upcomingLineupWarning: {
    defaultMessage: 'Upcoming <b>{competition}</b> lineup',
  },
  activeTaskAppearancesWarning: {
    defaultMessage: '<e>Active</e> <b>{mission}</b> mission',
  },
});

const TITLE_MAPS = {
  list: messages.listTitle,
  send_trade: messages.sendTradeTitle,
  receive_trade: messages.receiveTradeTitle,
  seal: messages.sealTitle,
  unseal: messages.unsealTitle,
};

type Props = {
  transferType: TransferType;
  openedLineups: string[];
  liveLineup?: string;
  lineupUrl?: string;
  activeTasks: {
    title: string;
    sealed: boolean | null;
  }[];
};

export const UsedCardWarning = ({
  openedLineups,
  liveLineup,
  lineupUrl,
  transferType,
  activeTasks,
}: Props) => {
  const lineupLink = (...text: ReactNode[]) => (
    <LinkOther target="_blank" rel="noreferrer" href={lineupUrl!}>
      {Bold(text)}
    </LinkOther>
  );

  if (
    openedLineups.length === 0 &&
    liveLineup === undefined &&
    activeTasks.length === 0
  ) {
    return null;
  }

  return (
    <WarningWrapper>
      <Row>
        <IconWrapper>
          <FontAwesomeIcon
            icon={iconAlert}
            color="var(--c-red-300)"
            size="sm"
          />
        </IconWrapper>
        <BodyS as="div">
          <FormattedMessage
            {...TITLE_MAPS[transferType]}
            values={{
              count: openedLineups.length + (liveLineup ? 1 : 0),
            }}
          />
        </BodyS>
      </Row>
      {!!liveLineup && (
        <Row>
          <BodyS as="div">
            <FormattedMessage
              {...messages.liveLineupWarning}
              values={{
                competition: liveLineup,
                b: lineupUrl ? lineupLink : Bold,
                e: ErrorBold,
              }}
            />
          </BodyS>
        </Row>
      )}
      {openedLineups.map(lineup => (
        <Row key={lineup}>
          <BodyS as="div">
            <FormattedMessage
              {...messages.upcomingLineupWarning}
              values={{
                competition: lineup,
                b: lineupUrl ? lineupLink : Bold,
              }}
            />
          </BodyS>
        </Row>
      ))}
      {activeTasks.map(task => (
        <Row key={task.title}>
          <BodyS as="div">
            <FormattedMessage
              {...messages.activeTaskAppearancesWarning}
              values={{
                mission: task.title,
                b: Bold,
                e: ErrorBold,
              }}
            />
          </BodyS>
        </Row>
      ))}
    </WarningWrapper>
  );
};
